'use client';

// Redacted job card shown on the public jobs board after a posting
// has closed. Surfaces social proof — "casting actually happens
// here" — without exposing the description, rate, audition lines,
// or any way to apply.
//
// Renders intentionally lower-contrast than active JobCard so the eye
// reads active opportunities first and closed cards as background.
// Not clickable as a whole (the detail page would re-leak the redacted
// fields); only the poster's name/avatar links out to their profile.

import Link from 'next/link';
import { Avatar, Card, Tag } from '@/components/ds';
import { formatTimestamp } from '@/lib/timestampUtils';

export interface ClosedJobCardJob {
  id: string;
  title: string;
  tags?: string[] | null;
  closed_at: string;
  poster?: {
    id: string;
    display_name?: string | null;
    username?: string | null;
    avatar_url?: string | null;
  } | null;
}

interface ClosedJobCardProps {
  job: ClosedJobCardJob;
}

export default function ClosedJobCard({ job }: ClosedJobCardProps) {
  const posterName =
    job.poster?.display_name?.trim() || job.poster?.username || 'A poster';
  const posterUsername = job.poster?.username || null;
  const closedRelative = formatTimestamp(job.closed_at, 'relative');

  return (
    <Card padded={false} className="overflow-hidden opacity-80">
      <div className="p-4 sm:p-5">
        {/* Header row: poster + "Closed" badge. Stacks safely on
            narrow screens; the badge sits inline with the title row
            on >=sm and wraps under it on <sm if the title runs long. */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <Avatar
              src={job.poster?.avatar_url || null}
              name={posterName}
              username={posterUsername}
              size={32}
            />
            <div className="min-w-0">
              {posterUsername ? (
                <Link
                  href={`/u/${posterUsername}`}
                  className="text-text-secondary text-xs font-medium hover:text-text-primary transition-colors truncate block"
                >
                  {posterName}
                </Link>
              ) : (
                <span className="text-text-secondary text-xs font-medium truncate block">
                  {posterName}
                </span>
              )}
              <span className="text-text-tertiary text-[11px]">
                Closed {closedRelative.toLowerCase()}
              </span>
            </div>
          </div>
          <Tag className="flex-shrink-0 uppercase tracking-wide">Closed</Tag>
        </div>

        {/* Redacted body — title only. */}
        <h3 className="text-sm sm:text-base font-medium text-text-secondary truncate">
          {job.title}
        </h3>

        {/* Tags stay visible — they're not sensitive and help the
            visual rhythm. Capped at 6 to keep mobile rows tidy. */}
        {job.tags && job.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {job.tags.slice(0, 6).map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
